<script setup lang="ts">
import {
  tpgIconDelete,
  tpgIconEdit,
  tpgIconForward,
  tpgIconMoreVert,
  tpgIconReply,
} from '@tpg/grammar-web-icons';
import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import {
  inject,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarMenuItemProps,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarMenuItem,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import { useConfirmModalStore } from '@nora-monorepo/ui-shared';

import type { MessageState } from '#nora-chat-ui/states';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useForwardMessageStore } from '#nora-chat-ui/stores/forward-message-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { chatCan } from '#nora-chat-ui/utils/permission';

interface MessageOptionsProps {
  messageState: MessageState;
}

const props = defineProps<MessageOptionsProps>();
const emit = defineEmits([
  'optionMenuOpened',
  'optionMenuClosed',
  'onItemClicked',
]);
const { t } = useI18n();
const userStore = useChatUserStore();
const conversationMessageStore = useConversationMessageStore();

const { user } = storeToRefs(userStore);
const { quoteMessage, editingMessage } = storeToRefs(conversationMessageStore);

const forwardMessageStore = useForwardMessageStore();
const confirmModalStore = useConfirmModalStore();

const { messageState } = toRefs(props);
const menuOpen = ref<boolean>(false);

const handleOptionMenuToggled = inject<(isOpen: boolean) => void>(
  'handleOptionMenuToggled',
);

watch(menuOpen, () => {
  if (handleOptionMenuToggled) {
    handleOptionMenuToggled(menuOpen.value);
  }
});

function onItemClick() {
  emit('onItemClicked');
}

const messageIsMine = messageState.value.message.sentBy.id === user?.value?.id;

const menuItems: GrammarMenuItemProps[] = [
  {
    label: t('messageActions.reply'),
    leadingIconTemplate: tpgIconReply,
    onSelect: () => {
      onItemClick();

      if (!quoteMessage.value) {
        quoteMessage.value = messageState.value;
      }
    },
  },
  {
    label: t('messageActions.forward'),
    leadingIconTemplate: tpgIconForward,
    onSelect: () => {
      onItemClick();

      forwardMessageStore.openForwardMessageModal(
        messageState.value.message.conversationId,
        messageState.value.message.id,
      );
    },
  },
];

const deleteMessageItem: GrammarMenuItemProps = {
  label: t('common.delete'),
  leadingIconTemplate: tpgIconDelete,
  onSelect: () => {
    onItemClick();
    confirmModalStore.showConfirmModal({
      isDelete: true,
      headerText: t('message.delete-message-modal-title'),
      bodyText:
        t('message.delete-message-modal-description'),
      onConfirm: () => {
        conversationMessageStore.deleteMessage(props.messageState);
      },
    });
  },
};

const editMessageItem: GrammarMenuItemProps = {
  label: t('messageActions.edit'),
  leadingIconTemplate: tpgIconEdit,
  onSelect: () => {
    onItemClick();

    if (!editingMessage.value) {
      editingMessage.value = {
        oldMessage: messageState.value,
      };
    }
  },
};

const messageIsForwarded = !!messageState.value.message.forwardedFromId;

if (messageIsMine) {
  if (!messageIsForwarded) {
    menuItems.push(editMessageItem);
  }

  menuItems.push(deleteMessageItem);
}
else {
  if (
    chatCan(
      'update:chat.conversation.message',
      messageState.value.message.conversationId,
    )
    && !messageIsForwarded
  ) {
    menuItems.push(editMessageItem);
  }

  if (
    chatCan(
      'delete:chat.conversation.message',
      messageState.value.message.conversationId,
    )
  ) {
    menuItems.push(deleteMessageItem);
  }
}

const messageActions = ref(menuItems);

const userMenu = ref<HTMLElement | null>(null);

onClickOutside(userMenu, () => {
  menuOpen.value = false;
});
</script>

<template>
  <GrammarQuaternaryMenuButton
    ref="userMenu"
    :icon-template="tpgIconMoreVert"
    @click="menuOpen = true"
  >
    <GrammarMenuItem
      v-for="(item, ix) in messageActions"
      :key="ix"
      v-bind="item"
    />
  </GrammarQuaternaryMenuButton>
</template>
